import { computed, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { TelemetryService } from 'src/app/core/telemetry/telemetry.service';
import {
  SnackbarBannerData,
} from 'src/app/shared/components/snackbar-banner/models/snackbar-banner.model';
import {
  SnackbarBannerComponent,
} from 'src/app/shared/components/snackbar-banner/snackbar-banner.component';

import { ERROR_MESSAGES } from '../constants/error-messages';
import {
  ParticipantSummary,
  SigningJoinCodeResponse,
} from '../models/signing-join-code.model';
import { SigningJoinCodeService } from '../services/signing-join-code.service';

interface ParticipantSelectionState {
  joinCode: string | null;
  signingMetadata: SigningJoinCodeResponse | null;
  participants: ParticipantSummary[];
  isLoading: boolean;
  error: string | null;
}

const initialState: ParticipantSelectionState = {
  joinCode: null,
  signingMetadata: null,
  participants: [],
  isLoading: false,
  error: null,
};

export const ParticipantSelectionStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    availableParticipants: computed(() =>
      store.participants().filter((p) => !p.isPresignComplete),
    ),
    allPresignComplete: computed(() => {
      const participants = store.participants();
      return participants.length > 0 && participants.every((p) => p.isPresignComplete);
    }),
    signingType: computed(() => store.signingMetadata()?.signingType ?? null),
  })),
  withMethods(
    (
      store,
      signingJoinCodeService = inject(SigningJoinCodeService),
      snackBar = inject(MatSnackBar),
      translationService = inject(TranslateService),
      telemetry = inject(TelemetryService),
    ) => ({
      async resolveJoinCode(joinCode: string): Promise<void> {
        if (!joinCode) {
          patchState(store, {
            isLoading: false,
            error: ERROR_MESSAGES.JOIN_CODE_NOT_PROVIDED,
          });
          throw new Error(ERROR_MESSAGES.JOIN_CODE_NOT_PROVIDED);
        }

        patchState(store, {
          isLoading: true,
          error: null,
          joinCode,
          signingMetadata: null,
          participants: [],
        });

        try {
          const response = await firstValueFrom(
            signingJoinCodeService.resolve(joinCode),
          );

          patchState(store, {
            signingMetadata: response,
            participants: response.participants,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          const errorMessage = translationService.instant(
            'participant-selection.errors.RESOLVE_JOIN_CODE_FAILED',
          );

          telemetry.trackException(
            {
              exception: error as Error,
              severityLevel: SeverityLevel.Error,
            },
            {
              component: 'ParticipantSelectionStore',
              method: 'resolveJoinCode',
              joinCode,
            },
          );

          patchState(store, {
            isLoading: false,
            error: errorMessage,
          });

          snackBar.openFromComponent<
            SnackbarBannerComponent,
            SnackbarBannerData
          >(SnackbarBannerComponent, {
            horizontalPosition: 'center',
            verticalPosition: 'top',
            data: {
              message: errorMessage,
              variant: 'warning',
            },
          });

          throw error;
        }
      },

      async refreshParticipantStatus(): Promise<void> {
        const currentJoinCode = store.joinCode();
        if (!currentJoinCode) {
          return;
        }

        try {
          const response = await firstValueFrom(
            signingJoinCodeService.resolve(currentJoinCode),
          );

          patchState(store, {
            participants: response.participants,
          });
        } catch (error) {
          telemetry.trackException(
            {
              exception: error as Error,
              severityLevel: SeverityLevel.Warning,
            },
            {
              component: 'ParticipantSelectionStore',
              method: 'refreshParticipantStatus',
              joinCode: currentJoinCode,
            },
          );
        }
      },

      clearError(): void {
        patchState(store, { error: null });
      },
    }),
  ),
);
